import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, IconButton, Button, Stack
} from '@mui/material'
import { NewReleases, Close, Notifications } from '@mui/icons-material'

interface WhatsNewItem {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  action?: { label: string; to: string }
}

const DISMISSED_KEY = 'connectloop-whats-new-dismissed'

function getDismissedIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]')
  } catch {
    return []
  }
}

function dismissItem(id: string) {
  const dismissed = getDismissedIds()
  if (!dismissed.includes(id)) {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed, id]))
  }
}

const allItems: WhatsNewItem[] = [
  {
    id: 'notifications-launch',
    icon: <Notifications sx={{ color: 'primary.main' }} />,
    title: 'Push Notifications Are Here!',
    description: 'Never miss a prayer request or discussion update. Enable push notifications to stay connected with your groups.',
    action: { label: 'Enable in Profile', to: '/profile' },
  },
]

export default function WhatsNew() {
  const navigate = useNavigate()
  const [dismissedIds, setDismissedIds] = useState(getDismissedIds)

  const visibleItems = allItems.filter(item => !dismissedIds.includes(item.id))

  if (visibleItems.length === 0) return null

  const handleDismiss = (id: string) => {
    dismissItem(id)
    setDismissedIds(prev => [...prev, id])
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        <NewReleases sx={{ verticalAlign: 'middle', mr: 1 }} />
        What&apos;s New
      </Typography>
      <Stack spacing={1}>
        {visibleItems.map(item => (
          <Card key={item.id} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ mt: 0.25 }}>{item.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {item.description}
                  </Typography>
                  {item.action && (
                    <Button
                      size="small"
                      sx={{ mt: 1, px: 0 }}
                      onClick={() => navigate(item.action!.to)}
                    >
                      {item.action.label}
                    </Button>
                  )}
                </Box>
                <IconButton size="small" aria-label="Dismiss" onClick={() => handleDismiss(item.id)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}
